import { api } from '@/lib/api';
import type {
  AddressRequest,
  BasicInfoRequest,
  DraftSummary,
  KycDocumentType,
  KycUploadResponse,
  PhotoUploadResponse,
  SavedAddress,
  SavedBasicInfo,
  StartRequest,
  StepResponse,
  SubmitResponse,
  TermsRequest,
  VendorPhotoType,
  PayoutRequest,
  PayoutView,
} from './types';
import type { VendorAgreement as ModuleAgreement } from '@/features/agreements/types';

const BASE = '/vendor/onboarding';

export async function fetch_draft(): Promise<DraftSummary | null> {
  try {
    const res = await api.get<DraftSummary>(`${BASE}/draft`);
    return res.data;
  } catch (err) {
    // 404 means no draft yet → caller can decide to call /start
    if (err instanceof Error && 'status' in err && (err as { status: number }).status === 404) {
      return null;
    }
    throw err;
  }
}

export async function start_onboarding(input: StartRequest): Promise<DraftSummary> {
  const res = await api.post<DraftSummary>(`${BASE}/start`, input);
  return res.data;
}

export async function save_basic_info(input: BasicInfoRequest): Promise<StepResponse> {
  const res = await api.post<StepResponse>(`${BASE}/basic-info`, input);
  return res.data;
}

export async function save_address(input: AddressRequest): Promise<StepResponse> {
  const res = await api.post<StepResponse>(`${BASE}/address`, input);
  return res.data;
}

export async function fetch_saved_basic_info(): Promise<SavedBasicInfo | null> {
  const res = await api.get<SavedBasicInfo | null>(`${BASE}/basic-info`);
  return res.data;
}

export async function fetch_saved_address(): Promise<SavedAddress | null> {
  const res = await api.get<SavedAddress | null>(`${BASE}/address`);
  return res.data;
}

export async function save_payout(input: PayoutRequest): Promise<StepResponse> {
  const res = await api.post<StepResponse>(`${BASE}/payout`, input);
  return res.data;
}

export async function fetch_saved_payout(): Promise<PayoutView | null> {
  const res = await api.get<PayoutView | null>(`${BASE}/payout`);
  return res.data;
}

export async function save_terms(input: TermsRequest): Promise<StepResponse> {
  const res = await api.post<StepResponse>(`${BASE}/terms`, input);
  return res.data;
}

export async function submit_onboarding(): Promise<SubmitResponse> {
  const res = await api.post<SubmitResponse>(`${BASE}/submit`);
  return res.data;
}

export async function upload_kyc(input: {
  type: KycDocumentType;
  file: File;
}): Promise<KycUploadResponse> {
  const form = new FormData();
  form.append('type', input.type);
  form.append('file', input.file);
  const res = await api.post<KycUploadResponse>('/vendor/uploads/kyc-docs', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function fetch_my_kyc_docs(): Promise<KycUploadResponse[]> {
  const res = await api.get<KycUploadResponse[]>('/vendor/uploads/kyc-docs');
  return res.data;
}

export async function fetch_my_photos(): Promise<PhotoUploadResponse[]> {
  const res = await api.get<PhotoUploadResponse[]>('/vendor/uploads/photos');
  return res.data;
}

export type PhotoVariant = 'thumb' | 'medium' | 'full';

export async function fetch_kyc_file_blob(doc_id: string): Promise<Blob> {
  const res = await api.get<Blob>(`/vendor/uploads/kyc-docs/${doc_id}/file`, {
    responseType: 'blob',
  });
  return res.data;
}

export async function fetch_photo_file_blob(
  photo_id: string,
  variant: PhotoVariant = 'thumb',
): Promise<Blob> {
  const res = await api.get<Blob>(`/vendor/uploads/photos/${photo_id}/file`, {
    responseType: 'blob',
    params: { variant },
  });
  return res.data;
}

export async function upload_photo(input: {
  type: VendorPhotoType;
  file: File;
  display_order?: number;
}): Promise<PhotoUploadResponse> {
  const form = new FormData();
  form.append('type', input.type);
  if (input.display_order !== undefined) {
    form.append('display_order', String(input.display_order));
  }
  form.append('file', input.file);
  const res = await api.post<PhotoUploadResponse>('/vendor/uploads/photos', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/**
 * Terms-step agreements: the current version of each of the vendor's module
 * agreements. Accepting the Terms step accepts every listed version server-side.
 * `download_url` / `return_email` are the older single-PDF fallback, used only
 * when `agreements` is empty.
 */
export type VendorAgreement = {
  available: boolean;
  agreements: ModuleAgreement[];
  download_url: string | null;
  return_email: string | null;
};

export async function fetch_agreement(): Promise<VendorAgreement> {
  const res = await api.get<VendorAgreement>(`${BASE}/agreement`);
  return { ...res.data, agreements: res.data.agreements ?? [] };
}
